import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { IndicadoresDoCalculo, TabelaDoCalculo } from '../componentes/ResultadoDoCalculo';
import { Botao, Cartao, EstadoCarregando, EstadoErro, EstadoVazio, Selo } from '../componentes/ui';
import type { LinhaCalculo, ResultadoCalculadora } from '../servicos/calculadora';
import { formatarCnpj, formatarCompetencia } from '../servicos/formato';
import type { ColunaDaPlanilha } from '../servicos/planilha';
import { requisicaoAutenticada } from '../servicos/http';
import { AnaliseDoPreCadastro, type PreCadastro } from './AnaliseDoPreCadastro';
import { rotuloDaEtapa, tomDaEtapa } from './Captacao';
import { Pagina } from './Carteira';

interface LinhaDaSimulacao extends LinhaCalculo {
  /** De onde veio o ICMS apurado: arquivo SPED ou digitado. */
  readonly origem: 'SPED' | 'MANUAL';
}

/** As colunas da tabela do cálculo, com a origem de cada competência e os valores como número. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<LinhaDaSimulacao>[] = [
  { titulo: 'Competência', largura: 12, valor: (l) => formatarCompetencia(l.competencia) },
  { titulo: 'Regime', largura: 16, valor: (l) => l.regime },
  { titulo: 'Origem', largura: 10, valor: (l) => (l.origem === 'SPED' ? 'SPED' : 'Digitado') },
  { titulo: 'ICMS apurado', formato: 'moeda', valor: (l) => l.totalSped },
  { titulo: '% utilização', formato: 'percentual', valor: (l) => l.percentSped },
  { titulo: 'Crédito utilizável', formato: 'moeda', valor: (l) => l.valueSped },
  { titulo: '% deságio', formato: 'percentual', valor: (l) => l.percentBeneficio },
  { titulo: 'Valor a pagar', formato: 'moeda', valor: (l) => l.total },
  { titulo: 'Economia', formato: 'moeda', valor: (l) => l.valueBeneficio },
];

interface SimulacaoDoRegistro {
  readonly registro: PreCadastro;
  readonly linhas: readonly LinhaDaSimulacao[];
  readonly totais: ResultadoCalculadora['totais'];
}

/**
 * A simulação de um lead: o cálculo que ele viu na calculadora pública.
 *
 * ## Os números são os gravados
 *
 * Nada é recalculado: é o que o lead viu, com a tabela de percentuais daquele
 * dia. É isso que o comercial precisa ter na frente ao ligar — o valor que a
 * pessoa já conhece, não um número novo que ela nunca viu.
 *
 * Indicadores e tabela são os mesmos componentes da calculadora
 * (`ResultadoDoCalculo`), com os mesmos rótulos.
 */
export function SimulacaoDoLead(): JSX.Element {
  const { registroId } = useParams<{ registroId: string }>();
  const [analisando, setAnalisando] = useState(false);

  // Sob `['pre-cadastros', …]`: aprovar ou rejeitar na análise já invalida essa
  // chave, e a etapa aqui se atualiza junto com a lista.
  const consulta = useQuery({
    queryKey: ['pre-cadastros', 'simulacao', registroId],
    queryFn: () =>
      requisicaoAutenticada<SimulacaoDoRegistro>(`/register/${registroId ?? ''}/simulacao`),
    enabled: registroId !== undefined,
  });

  if (consulta.isLoading) return <EstadoCarregando contexto="a simulação" />;
  if (consulta.isError) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar a simulação"
        mensagem={consulta.error.message}
        aoTentarNovamente={() => void consulta.refetch()}
      />
    );
  }

  const dados = consulta.data;
  if (dados === undefined) return <EstadoVazio titulo="Simulação não encontrada" />;

  const r = dados.registro;
  const decidido = r.aprovado || r.rejeitado;

  return (
    <Pagina
      titulo={r.legalName ?? r.personFullName ?? 'Simulação'}
      descricao={descricaoDaOrigem(dados.linhas)}
      acao={
        <Link
          to="/simulacoes"
          className="text-sm text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
        >
          ← Voltar para simulações
        </Link>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Cartao
          como="section"
          titulo="Contato"
          acao={<Selo tom={tomDaEtapa(r)}>{r.rejeitado ? 'Rejeitado' : rotuloDaEtapa(r)}</Selo>}
        >
          <dl className="grid gap-4 sm:grid-cols-2">
            <Dado rotulo="Nome" valor={r.personFullName} />
            <Dado rotulo="Vínculo com a empresa" valor={r.personOccupation ?? null} />
            <Dado rotulo="E-mail" valor={r.personEmailAddress} />
            <Dado rotulo="Telefone" valor={r.personPhone} />
          </dl>
        </Cartao>

        <Cartao
          como="section"
          titulo="Empresa"
          acao={
            decidido ? undefined : (
              <Botao tamanho="pequeno" onClick={() => setAnalisando(true)}>
                Analisar cadastro
              </Botao>
            )
          }
        >
          <dl className="grid gap-4 sm:grid-cols-2">
            <Dado rotulo="Razão social" valor={r.legalName} />
            <Dado rotulo="CNPJ" valor={r.taxpayerId === null ? null : formatarCnpj(r.taxpayerId)} />
            <Dado
              rotulo="Cidade"
              valor={
                r.addressCity === null
                  ? null
                  : `${r.addressCity}${r.addressStateCode === null ? '' : `/${r.addressStateCode}`}`
              }
            />
            <Dado rotulo="Regime" valor={regimeDe(dados.linhas)} />
          </dl>
        </Cartao>
      </div>

      {dados.linhas.length === 0 ? (
        <EstadoVazio
          titulo="Este lead não chegou a simular"
          descricao="Ele deixou o contato, mas nenhuma competência foi calculada."
        />
      ) : (
        <section aria-labelledby="titulo-calculo" className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2
              id="titulo-calculo"
              className="font-display text-lg font-semibold tracking-tight text-conteudo-primario"
            >
              Resultado da simulação
            </h2>
            <BotaoExtrairXlsx
              titulo={`Simulação - ${r.legalName ?? r.personFullName ?? 'lead'}`}
              tamanho="pequeno"
              colunas={COLUNAS_DA_PLANILHA}
              linhas={dados.linhas}
            />
          </div>
          <IndicadoresDoCalculo totais={dados.totais} />
          <TabelaDoCalculo
            linhas={dados.linhas}
            totais={dados.totais}
            legenda="Simulação do lead por competência"
          />
        </section>
      )}

      <AnaliseDoPreCadastro
        registro={analisando ? r : null}
        aoFechar={() => setAnalisando(false)}
      />
    </Pagina>
  );
}

/** "Calculado por SPED", "Lançamento manual" — de onde veio o ICMS apurado. */
function descricaoDaOrigem(linhas: readonly LinhaDaSimulacao[]): string {
  if (linhas.length === 0) return 'Simulação da calculadora pública.';
  const porSped = linhas.filter((l) => l.origem === 'SPED').length;
  const meses = `${linhas.length} ${linhas.length === 1 ? 'mês' : 'meses'}`;
  if (porSped === linhas.length) return `Calculado a partir do SPED · ${meses}.`;
  if (porSped === 0) return `ICMS apurado lançado manualmente · ${meses}.`;
  return `Parte pelo SPED, parte lançada manualmente · ${meses}.`;
}

function regimeDe(linhas: readonly LinhaDaSimulacao[]): string | null {
  const regimes = new Set(linhas.map((l) => l.regime));
  if (regimes.size === 0) return null;
  if (regimes.size > 1) return 'Industrial e comercial';
  return regimes.has('INDUSTRIAL') ? 'Industrial' : 'Comercial';
}

function Dado(props: { readonly rotulo: string; readonly valor: string | null }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">
        {props.valor === null || props.valor === '' ? '—' : props.valor}
      </dd>
    </div>
  );
}
